import React, { useState } from "react";
import styles from "./change-password-drawer.module.css";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { changePassword } from "../../../../../../services/auth";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import CustomTextfield from "../../../../../../common/input/custom-textfield/custom-textfield";
import CustomButton from "../../../../../../common/custom-button/custom-button";
import CustomDrawer from "../../../../../../common/custom-drawer/custom-drawer";
import { Col, Row } from "antd";
import useLogout from "../../../../../../hooks/useLogout";
import PasswordRules from "../../../../../../components/password-rules/password-rules";
import { PASSWORD_REGEX } from "../../../../../../constants/common";

const defaultInputValues = {
  old_password: "",
  new_password: "",
  confirm_password: "",
}

const defaultVisibility = {
  current: false,
  new: false,
  confirm: false,
}

const ChangePasswordDrawer = ({
  isModalOpen,
  onClose,
}) => {
  const logout = useLogout();
  const [loading, setLoading] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState(defaultVisibility);
  const { control, handleSubmit, reset, watch } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: defaultInputValues
  });
  const newPassword = watch("new_password", "");

  const togglePassword = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        old_password: values?.old_password,
        new_password: values?.new_password,
      };
      await changePassword(payload);
      toast.success("Password changed successfully, please login with new password");
      setTimeout(() => logout(), 500);
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "Error! please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPasswordVisibility(defaultVisibility);
    reset(defaultInputValues);
    onClose();
  };

  return (
    <CustomDrawer
      title="Change Password"
      open={isModalOpen}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          <CustomButton
            title="Update password"
            size="large"
            loading={loading}
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      }
      width={500}
      drawerStyles={{ body: { padding: "32px" } }}
    >
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Controller
            name="old_password"
            control={control}
            rules={{ required: true }}
            render={({
              field: { onChange, value },
              fieldState: { error },
            }) => (
              <CustomTextfield
                id="old_password"
                label="Current Password"
                showRequired
                labelColor="#434343"
                type={passwordVisibility.current ? "text" : "password"}
                suffix={
                  passwordVisibility.current ? (
                    <EyeTwoTone onClick={() => togglePassword("current")} />
                  ) : (
                    <EyeInvisibleOutlined
                      onClick={() => togglePassword("current")}
                    />
                  )
                }
                placeholder="Enter current password"
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              />
            )}
          />
        </Col>

        <Col span={24}>
          <Controller
            name="new_password"
            control={control}
            rules={{
              required: true,
              minLength: {
                value: 8,
                message: "Minimum 8 characters required",
              },
              pattern: {
                value: PASSWORD_REGEX,
                message: "Must include uppercase, lowercase, number & special character",
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomTextfield
                id="new_password"
                label="New Password"
                showRequired
                labelColor="#434343"
                type={passwordVisibility.new ? "text" : "password"}
                suffix={
                  passwordVisibility.new ? (
                    <EyeTwoTone onClick={() => togglePassword("new")} />
                  ) : (
                    <EyeInvisibleOutlined
                      onClick={() => togglePassword("new")}
                    />
                  )
                }
                placeholder="Enter a new password"
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
                errorMessage={error?.message}
              />
            )}
          />
        </Col>

        <Col span={24}>
          <Controller
            name="confirm_password"
            control={control}
            rules={{
              required: true,
              validate: (value) =>
                value === newPassword || "Passwords do not match",
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomTextfield
                id="confirm_password"
                label="Confirm Password"
                showRequired
                labelColor="#434343"
                type={passwordVisibility.confirm ? "text" : "password"}
                suffix={
                  passwordVisibility.confirm ? (
                    <EyeTwoTone onClick={() => togglePassword("confirm")} />
                  ) : (
                    <EyeInvisibleOutlined
                      onClick={() => togglePassword("confirm")}
                    />
                  )
                }
                placeholder="Confirm new password"
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
                errorMessage={error?.message}
              />
            )}
          />

        </Col>

        <Col span={24}>
          <PasswordRules password={newPassword} />
        </Col>
      </Row>
    </CustomDrawer>
  )
};

export default ChangePasswordDrawer;